/**
 * Per-agent blast radius, fetched once for a page of agent cards.
 *
 * `/attack-paths/exposure` answers for the whole estate in one call. An agent
 * card rendered thirty times must not make thirty requests, so this follows
 * `useEntitlements`' shape exactly: a module-level cache keyed by tenant, an
 * inflight map so simultaneous mounts share one promise, and a short TTL.
 *
 * Three states, and the third is the one that matters here
 * --------------------------------------------------------
 *   loading       nothing to say yet
 *   notEntitled   the route 404'd; show the bundle teaser, not an error
 *   byHint        a map, where an ABSENT hint is NOT a zero blast radius
 *
 * That last distinction is load-bearing. The endpoint reads the LATEST scan
 * per repository (`reach_query._current_analysis_ids`), and `agent_id_hint`
 * embeds the line number of the agent's definition — it is a within-scan key,
 * not a cross-scan one. So an agent on an older analysis's page, or one whose
 * definition moved a line since the current scan, simply will not be in the
 * map. Rendering that as "influences nothing" would be a claim; it has to read
 * as "not computed for this agent".
 *
 * An agent that genuinely reaches nothing IS in the map, with
 * `component_count: 0` — the endpoint reports it rather than omitting it, for
 * exactly this reason.
 */
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { attackPathsApi, CounterfactualAgent } from '../services/api'

type ByHint = Record<string, CounterfactualAgent>

interface Result {
  byHint: ByHint | null
  loading: boolean
  notEntitled: boolean
}

const cache = new Map<string, { at: number; value: ByHint | null }>()
const inflight = new Map<string, Promise<ByHint | null>>()

const TTL_MS = 60_000

export const clearExposureCache = () => {
  cache.clear()
  inflight.clear()
}

/** `null` distinguishes "refused" from "fetched and empty", which an empty map
 *  could not. */
const fetchExposure = (tenantId: string): Promise<ByHint | null> => {
  const hit = cache.get(tenantId)
  if (hit && Date.now() - hit.at < TTL_MS) return Promise.resolve(hit.value)

  const existing = inflight.get(tenantId)
  if (existing) return existing

  const p = attackPathsApi
    .exposure()
    .then((resp) => {
      const map: ByHint = {}
      ;(resp.data.agents || []).forEach((a) => {
        map[a.agent_id_hint] = a
      })
      cache.set(tenantId, { at: Date.now(), value: map })
      return map as ByHint | null
    })
    .catch(() => {
      // A 404 is the ordinary case for an unentitled tenant. Cached as null so
      // a page of thirty cards does not retry thirty times.
      cache.set(tenantId, { at: Date.now(), value: null })
      return null
    })
    .finally(() => {
      inflight.delete(tenantId)
    })

  inflight.set(tenantId, p)
  return p
}

export const useCounterfactualExposure = (): Result => {
  const { user } = useSelector((state: RootState) => state.auth)
  const tenantId = user?.tenant_id
  const [byHint, setByHint] = useState<ByHint | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!tenantId) {
      setLoading(false)
      return
    }
    let cancelled = false
    fetchExposure(tenantId).then((map) => {
      if (cancelled) return
      setByHint(map)
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [tenantId])

  return { byHint, loading, notEntitled: !loading && byHint === null }
}
